import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function RenewalConfirmBanner({ contract, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  if (!contract) return null;
  if (!contract.auto_renewal) return null;
  const confirmed = contract.renewal_confirmation?.decision;

  // Only show banner if within 90 days of expiration OR already confirmed.
  const exp = contract.expiration_date;
  if (!exp) return null;
  const days = Math.floor((new Date(exp) - new Date()) / (1000 * 60 * 60 * 24));
  if (days > 90 && !confirmed) return null;

  const submit = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/renewal-confirm`, { decision, notes });
      toast.success(decision === "renew" ? "Marked to renew" : "Marked to terminate");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (confirmed) {
    return (
      <div className={`rounded-md border p-3 mb-4 flex items-center gap-2 text-sm ${
        confirmed === "renew" ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-red-50 border-red-300 text-red-900"
      }`} data-testid="renewal-confirm-done">
        {confirmed === "renew" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        Renewal decision recorded: <b>{confirmed === "renew" ? "Still in use — allow renewal" : "Terminate — do not renew"}</b>
        {contract.renewal_confirmation?.notes && <span className="text-xs">· {contract.renewal_confirmation.notes}</span>}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="renewal-confirm-banner">
      <div className="font-semibold text-amber-900 mb-1">Auto-renew confirmation needed ({days} days to expiration)</div>
      <div className="text-xs text-amber-800 mb-3">This contract auto-renews on {(exp || "").slice(0, 10)}. Please confirm whether it should renew or terminate.</div>
      <textarea className="w-full text-sm border border-amber-300 rounded p-2 mb-2" rows={2} placeholder="Optional owner notes for Legal…" value={notes} onChange={e => setNotes(e.target.value)} data-testid="renewal-confirm-notes" />
      <div className="flex gap-2">
        <button disabled={busy} className="text-sm px-4 py-2 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 disabled:opacity-50" onClick={() => submit("renew")} data-testid="renewal-confirm-renew">Still in use — allow renewal</button>
        <button disabled={busy} className="text-sm px-4 py-2 bg-red-600 text-white rounded font-semibold hover:bg-red-700 disabled:opacity-50" onClick={() => submit("terminate")} data-testid="renewal-confirm-terminate">Terminate — do not renew</button>
      </div>
    </div>
  );
}
